import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import HomeRoute from './components/HomeRoute';
import LoginForm from './components/auth/LoginForm';
import SignupForm from './components/auth/SignupForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import ResetPasswordAction from './components/auth/ResetPasswordAction';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminDashboard from './pages/admin/AdminDashboard';
import Users from './pages/admin/Users';
import CreateUser from './pages/admin/CreateUser';
import LeaguesList from './pages/admin/leagues/LeaguesList';
import AdminLeagueDetail from './pages/admin/leagues/LeagueDetail';
import SeasonPlayers from './pages/admin/leagues/SeasonPlayers';
import LeaguePredictions from './pages/admin/leagues/LeaguePredictions';
import AdminCreateLeague from './pages/admin/leagues/CreateLeague';
import SeasonList from './pages/admin/seasons/SeasonList';
import CreateSeason from './pages/admin/seasons/CreateSeason';
import SeasonDetail from './pages/admin/seasons/SeasonDetail';
import WeekDetail from './pages/admin/seasons/WeekDetail';
import WeekPicks from './pages/admin/seasons/WeekPicks';
import WeekManagement from './pages/admin/seasons/WeekManagement';
import TribeManagement from './pages/admin/seasons/TribeManagement';
import LeagueManagerDashboard from './pages/league-manager/LeagueManagerDashboard';
import CreateLeague from './pages/league-manager/CreateLeague';
import LeagueDetail from './pages/league-manager/LeagueDetail';
import LeagueStandings from './pages/league-manager/LeagueStandings';
import LeagueWeekSummary from './pages/league-manager/LeagueWeekSummary';
import EmailRecap from './pages/league-manager/EmailRecap';
import ScoringRules from './pages/league-manager/ScoringRules';
import TeamsManagement from './pages/league-manager/TeamsManagement';
import ManageDraft from './pages/league-manager/ManageDraft';
import PredictionCollection from './pages/public/PredictionCollection';
import PredictionCollection2 from './pages/public/PredictionCollection2';
import PredictionCollection3 from './pages/public/PredictionCollection3';
import ClaimTeam from './pages/player/ClaimTeam';
import TeamDetail from './pages/player/TeamDetail';
import PlayerStandings from './pages/player/Standings';
import PredictionHistory from './pages/player/PredictionHistory';
import PlayerDraft from './pages/player/PlayerDraft';
import ProfileSettings from './pages/ProfileSettings';
import { USER_ROLES } from './models/dataModels';
import './App.css';

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <Toaster position="top-right" />
          <Navigation />
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/signup" element={<SignupForm />} />
            <Route path="/auth/action" element={<ResetPasswordAction />} />
            
            {/* Public Routes */}
            <Route path="/leagues" element={<div className="p-8 text-center">Leagues page coming soon!</div>} />
            <Route path="/contestants" element={<div className="p-8 text-center">Contestants page coming soon!</div>} />
            <Route path="/predictions/:leagueId/:seasonId" element={<PredictionCollection />} />
            <Route path="/predictions2/:leagueId/:seasonId" element={<PredictionCollection2 />} />
            <Route path="/predictions3/:leagueId/:seasonId" element={<PredictionCollection3 />} />
            
            {/* Player Routes */}
            <Route path="/player/claim-team/:leagueId/:seasonId" element={
              <ProtectedRoute>
                <ClaimTeam />
              </ProtectedRoute>
            } />
            <Route path="/player/draft/:leagueId/:seasonId" element={
              <ProtectedRoute>
                <PlayerDraft />
              </ProtectedRoute>
            } />
            
            {/* League Manager Routes */}
            <Route path="/manage-leagues" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <LeagueManagerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/manage-leagues/create" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <CreateLeague />
              </ProtectedRoute>
            } />
            <Route path="/manage-leagues/:leagueId" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <LeagueDetail />
              </ProtectedRoute>
            } />
            <Route path="/manage-leagues/:leagueId/scoring/:seasonId" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <ScoringRules />
              </ProtectedRoute>
            } />
            <Route path="/manage-leagues/:leagueId/teams/:seasonId" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <TeamsManagement />
              </ProtectedRoute>
            } />
            <Route path="/manage-leagues/:leagueId/draft/:seasonId" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <ManageDraft />
              </ProtectedRoute>
            } />
            <Route path="/manage-leagues/:leagueId/predictions" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <LeaguePredictions />
              </ProtectedRoute>
            } />
            <Route path="/manage-leagues/:leagueId/standings/:seasonId" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <LeagueStandings />
              </ProtectedRoute>
            } />
            <Route path="/manage-leagues/:leagueId/season/:seasonId/week/:weekNumber" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <LeagueWeekSummary />
              </ProtectedRoute>
            } />
            <Route path="/manage-leagues/:leagueId/season/:seasonId/week/:weekNumber/email-recap" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <EmailRecap />
              </ProtectedRoute>
            } />
            <Route path="/league-settings" element={
              <ProtectedRoute requiredRole={USER_ROLES.LEAGUE_MANAGER}>
                <div className="p-8 text-center">League Settings page coming soon!</div>
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <Users />
              </ProtectedRoute>
            } />
            <Route path="/admin/users/create" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <CreateUser />
              </ProtectedRoute>
            } />
            <Route path="/admin/seasons" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <SeasonList />
              </ProtectedRoute>
            } />
            <Route path="/admin/seasons/create" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <CreateSeason />
              </ProtectedRoute>
            } />
            <Route path="/admin/seasons/:seasonId/weeks" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <WeekManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/seasons/:seasonId/week/:weekNumber" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <WeekDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/seasons/:seasonId/week/:weekNumber/picks" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <WeekPicks />
              </ProtectedRoute>
            } />
            <Route path="/admin/seasons/:seasonId" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <SeasonDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/seasons/:seasonId/tribes" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <TribeManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/seasons/:seasonId/edit" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <div className="p-8 text-center">Edit Season page coming soon!</div>
              </ProtectedRoute>
            } />
            <Route path="/admin/contestants" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <div className="p-8 text-center">Manage Contestants page coming soon!</div>
              </ProtectedRoute>
            } />
            <Route path="/admin/leagues" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <LeaguesList />
              </ProtectedRoute>
            } />
            <Route path="/admin/leagues/create" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <AdminCreateLeague />
              </ProtectedRoute>
            } />
            <Route path="/admin/leagues/:leagueId" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <AdminLeagueDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/leagues/:leagueId/seasons/:seasonId" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <SeasonPlayers />
              </ProtectedRoute>
            } />
            <Route path="/admin/leagues/:leagueId/predictions" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <LeaguePredictions />
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                <div className="p-8 text-center">System Settings page coming soon!</div>
              </ProtectedRoute>
            } />
            
            {/* Player Routes */}
            <Route path="/player/teams/:teamId" element={
              <ProtectedRoute>
                <TeamDetail />
              </ProtectedRoute>
            } />
            <Route path="/player/standings/:leagueId/:seasonId" element={
              <ProtectedRoute>
                <PlayerStandings />
              </ProtectedRoute>
            } />
            <Route path="/player/history/:teamId" element={
              <ProtectedRoute>
                <PredictionHistory />
              </ProtectedRoute>
            } />
            
            {/* Other Routes */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfileSettings />
              </ProtectedRoute>
            } />
            <Route path="/create-league" element={<div className="p-8 text-center">Create League page coming soon!</div>} />
            <Route path="/forgot-password" element={<ForgotPasswordForm />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
